import { Box, Flex, Text } from '@mezzoforte/forge';
import { useContext } from 'react';
import { BiddingContext } from '@/context/BiddingContext';
import { PriceWithVat } from '@/types/Price';
import { groupBy } from '@/util/collection';
import { formatCurrency } from '@/util/currency';
import { vatAmount } from '@/util/price';
import { DeliveryType, ReceiptType } from '@/types/ListEntry';

interface CostRow {
  price: PriceWithVat;
  label: string;
}

export interface BidCostsBreakdownProps {
  bidValue: number;
  selectedDeliveryType: DeliveryType | null;
  selectedReceiptType: ReceiptType | null;
  isAutobid?: boolean;
}

const sum = (a: number, b: number) => a + b;

const totalCostWithVat = (rows: CostRow[]) => rows.map((r) => r.price.amount).reduce(sum);
const totalCostWithoutVat = (rows: CostRow[]) =>
  rows.map((r) => r.price.amount / (1 + r.price.vatPercent / 100)).reduce(sum);

function VatRows({ costsBreakdown, receiptType }: { costsBreakdown: CostRow[]; receiptType: ReceiptType | null }) {
  const breakdown = groupBy(
    costsBreakdown.map((r) => ({ ...r, vatPercent: r.price.vatPercent })),
    ({ price }) => price.vatPercent
  );

  const rows = [...breakdown.entries()].map(([vatPercent, row]) => ({
    vatPercent,
    vatAmount: row.reduce((sum, i) => sum + vatAmount(i.price).amount, 0),
  }));

  return (
    <>
      {rows.map((r) => (
        <Flex
          key={r.vatPercent}
          justify="space-between"
        >
          <Flex as="dt">
            {receiptType === ReceiptType.COMPANY ? '+' : 'sis.'} alv. {r.vatPercent} %
          </Flex>
          <Flex as="dd">{formatCurrency(r.vatAmount, false)}</Flex>
        </Flex>
      ))}
    </>
  );
}

export default function BidCostsBreakdown({
  bidValue,
  selectedDeliveryType,
  selectedReceiptType,
  isAutobid,
}: BidCostsBreakdownProps) {
  const context = useContext(BiddingContext);

  const costsBreakdown: CostRow[] = (() => {
    const bidCost = () => ({
      price: { amount: bidValue, vatPercent: context.vatPerc },
      label: isAutobid ? 'Maksimitarjouksesi' : 'Tarjouksesi',
    });
    const deliveryCost = () => {
      if (!context.deliveryMethods.length) return null;
      const deliveryType =
        context.deliveryMethods.length === 1 ? context.deliveryMethods[0].type : selectedDeliveryType;
      const deliveryMethod = context.deliveryMethods.find((m) => m.type === deliveryType);
      if (!deliveryMethod) return null;

      return {
        price: {
          amount: deliveryMethod.expenses ?? 0,
          vatPercent: context.vatPerc,
        },
        label: deliveryMethod.expensesLabel,
      };
    };
    const processingCost = () => {
      if (context.processingCostInfo === null) return null;
      return {
        price: {
          amount: context.processingCostInfo.sum,
          vatPercent: context.vatPerc,
        },
        label: context.processingCostInfo.title,
      };
    };

    return [bidCost(), deliveryCost(), processingCost()].filter((c) => c !== null) as unknown as CostRow[];
  })();

  return (
    <Box
      as="dl"
      mt={{ md: 5 }}
      data-test="cost-breakdown"
    >
      {costsBreakdown.map((row) => (
        <Flex
          key={row.label}
          justify="space-between"
          gap={3}
        >
          <Flex
            as="dt"
            align="baseline"
            flexWrap="wrap"
            columnGap={2}
          >
            <Text>{row.label}</Text>
            <Text variant="requiredbylaw">(alv. {row.price.vatPercent} %)</Text>
          </Flex>
          <Box
            as="dd"
            data-test={`cost_${row.label}`}
          >
            {formatCurrency(row.price.amount, false)}
          </Box>
        </Flex>
      ))}
      <Flex
        justify="space-between"
        gap={3}
        mt={3}
      >
        <Text
          as="dt"
          variant="bold"
        >
          Yhteensä {selectedReceiptType === ReceiptType.COMPANY && <span>(veroton)</span>}
        </Text>
        <Text
          as="dd"
          variant="bold"
          data-test="total-cost"
        >
          {formatCurrency(
            selectedReceiptType === ReceiptType.COMPANY
              ? totalCostWithoutVat(costsBreakdown)
              : totalCostWithVat(costsBreakdown),
            false
          )}
        </Text>
      </Flex>
      <VatRows
        costsBreakdown={costsBreakdown}
        receiptType={selectedReceiptType}
      />
    </Box>
  );
}
